import React, { useLayoutEffect, useCallback, useEffect, useState } from "react"
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import SplitText from "gsap/SplitText"

import Firebase from "../services/FirebaseService"

import Nav from "../components/nav/Nav"
import StarRating from "../components/starRating/StarRating"
import AppStoreButton from "../components/appStoreButton/AppStoreButton"
import CardSwiper from "../components/cards/swiper/CardSwiper"
import DeckGrid from "../components/decks/grid/DeckGrid"
import Counter from "../components/counter/Counter"
import BackgroundImage from "../components/backgroundImage/BackgroundImage"
import MarqueeText from "../components/marqueeText/MarqueeText"
import Testimonials from "../components/testimonials/Testimonials"
import SEO from "../components/seo"

import teamMemberIcon from "../assets/icons/tm.svg"
import agileCoachIcon from "../assets/icons/ac.svg"
import leaderIcon from "../assets/icons/le.svg"
import professionalCoachIcon from "../assets/icons/pc.svg"
import scrumMasterIcon from "../assets/icons/sm.svg"
import productOwnerIcon from "../assets/icons/po.svg"
import geoff from "../assets/brand/geoff.png"
import phoneImg from "../assets/images/android-screenshot.jpg"
import appIcon from "../assets/brand/app-icon.png"
import iaLogo from "../assets/brand/logo.svg"

import "../styles/global.scss"
import "../styles/typography.scss"
import styles from "./index.module.scss"

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(SplitText)

export enum COLOURS {
  YELLOW = "#EFBE47",
  PURPLE = "#6670A0",
  DARK = "#31364d",
  GREEN = "#9DD091",
  EXTRADARK = "#282c3e",
  OFFWHITE = "#fbfbfb",
}

const IndexPage = () => {
  const [cardCount, setCardCount] = useState<number | undefined>(undefined)
  const [deckCount, setDeckCount] = useState<number | undefined>(undefined)
  const [freeCount, setFreeCount] = useState<number | undefined>(undefined)

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchMetrics()
  }, [])

  useLayoutEffect(() => {
    const h1Split = new SplitText("#header-h1", { type: "words" })
    gsap.set("#header-h1", { opacity: 1 })
    gsap.fromTo(
      h1Split.words,
      { opacity: 0, y: i => (i % 2 ? 24 : -24) },
      {
        duration: 1.2,
        opacity: 1,
        y: 0,
        stagger: { from: "random", amount: 0.8 },
        onComplete: () => {
          gsap.fromTo("#header-h2", { opacity: 0, y: -24 }, { opacity: 1, y: 0 })
          h1Split.revert()
        },
      }
    )
  }, [])

  // Benefit Blocks
  useEffect(() => {
    ScrollTrigger.matchMedia({
      // desktop
      "(min-width: 768px)": () => {
        gsap.fromTo(
          ".benefit-card",
          {
            rotate: 0,
            yPercent: 50,
            xPercent: i => (i === 0 ? 100 : i === 2 ? -100 : 0),
          },
          {
            rotate: i => (i === 0 ? -4 : i === 2 ? 4 : 0),
            yPercent: i => (i === 1 ? -10 : 0),
            xPercent: 0,
            ease: "ease-in",
            scrollTrigger: {
              trigger: "#benefit-cards",
              start: "bottom bottom",
              end: "center center",
              scrub: 1,
            },
          }
        )
      },
      // mobile - handled differently because they're stacked vertically
      "(max-width: 767px)": () => {
        gsap.utils.toArray(".benefit-card").forEach((block, i: number) => {
          gsap.fromTo(
            block as HTMLElement,
            { xPercent: i % 2 ? 120 : -120 },
            {
              xPercent: 0,
              ease: "none",
              scrollTrigger: {
                trigger: block as HTMLElement,
                start: "center bottom",
                end: "+=252px",
                scrub: true,
              },
            }
          )
        })
      },
    })
  }, [])

  useEffect(() => {
    // Team Roles
    gsap.to(".team-role", {
      opacity: 1,
      duration: 0.6,
      stagger: 0.1,
      scrollTrigger: {
        trigger: "#roles",
        start: "bottom bottom",
        end: "bottom top",
        toggleActions: "play none none reverse",
      },
    })
    // // Screenshot (ratings section)
    // ScrollTrigger.matchMedia({
    //   "(min-width: 768px)": () => {
    //     gsap.fromTo(
    //       "#phone-screenshot",
    //       { yPercent: 100 },
    //       {
    //         yPercent: 0,
    //         scrollTrigger: {
    //           trigger: "#ratings-section",
    //           start: "center bottom",
    //           scrub: true,
    //         },
    //       }
    //     )
    //   },
    //   // Mobile transition from the side
    //   "(max-width: 767px)": () => {
    //     gsap.fromTo(
    //       "#phone-screenshot",
    //       { xPercent: 50 },
    //       {
    //         xPercent: 0,
    //         scrollTrigger: {
    //           trigger: "#ratings-section",
    //           start: "center bottom",
    //           scrub: true,
    //         },
    //       }
    //     )
    //   },
    // })
  }, [])

  const fetchMetrics = useCallback(async () => {
    const { cardCount, deckCount, freeCount } = await Firebase.fetchMetrics()
    setCardCount(cardCount)
    setDeckCount(deckCount)
    setFreeCount(freeCount)
  }, [])

  return (
    <main>
      <SEO title="Free Download" />
      <Nav />

      {/* Header and Card Swiper */}
      <section>
        <header className={styles.header}>
          <BackgroundImage />
          <h1 id="header-h1">Your Own Agile Coach In Your Pocket</h1>
          <h2 id="header-h2">
            Move From Good to Great with <mark>1000+</mark> Agile Coaching Cards at Your Finger Tips
          </h2>
        </header>

        <CardSwiper />
      </section>

      {/* Benefits */}
      <section id="benefit-cards" className={styles.benefitCards}>
        {BENEFITS_CARDS.map(card => (
          <div className="benefit-card" key={card.heading}>
            {/* <img src={card.icon} alt={card.heading} /> */}
            <h3>{card.heading}</h3>
            <p>{card.text}</p>
          </div>
        ))}
      </section>

      {/* Team Roles */}
      <section id="roles" className={styles.roles}>
        <div>
          {TEAM_ROLES.map((role, i: number) => (
            <div
              key={role.alt}
              className="team-role"
              style={{
                transform: `rotate(${i * 60}deg) translateY(-150%) rotate(-${i * 60}deg)`,
              }}
            >
              <img src={role.icon} alt={role.alt} />
              <span>{role.alt}</span>
            </div>
          ))}
        </div>
        <div className="large team-role" style={{ opacity: 0 }}>
          <span>6</span> Team Roles
        </div>
      </section>

      {/* Decks */}
      <section className={styles.decksSection}>
        <div className={styles.counters}>
          {deckCount && (
            <Counter
              label="Decks"
              number={deckCount}
              countAmount={3}
              backgroundColour={COLOURS.YELLOW}
              textColour={COLOURS.EXTRADARK}
            />
          )}
          {cardCount && (
            <Counter
              label="Cards"
              number={cardCount}
              countAmount={7}
              backgroundColour={COLOURS.PURPLE}
              textColour={COLOURS.OFFWHITE}
            />
          )}
          {freeCount && (
            <Counter
              label="Freebies"
              number={freeCount}
              countAmount={5}
              backgroundColour={COLOURS.GREEN}
              textColour={COLOURS.EXTRADARK}
            />
          )}
        </div>
        <DeckGrid />
      </section>

      {/* Ratings and Download */}
      <section id="ratings-section" className={styles.ratingsSection}>
        <img id="phone-screenshot" src={phoneImg} alt="Agile Cards on Android" />
        {/* Add Grid Class to Layout.scss */}
        <div>
          <div className={styles.ratingBlock}>
            <img src={appIcon} alt="App Icon" />
            <div>
              <StarRating show={true} rating={4.5} height={28} gap={4} colour="#efbe47" />
              <span>from 50+ reviews</span>
            </div>
          </div>
          <h4>
            1000’s of Agile Coaches, Scrum Masters and Product Owners use Agile Coaching Cards to
            Grow their Teams
          </h4>
          <div className={styles.appStoreButtons}>
            <AppStoreButton isAppleStore={false} />
            <AppStoreButton isAppleStore={true} />
          </div>
          <p>Download now and access {freeCount || "hundreds of"} free cards!</p>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* About Geoff */}
      <section className={styles.aboutGeoffSection}>
        <MarqueeText text="FROM GOOD TO GREAT." />
        <div>
          <img src={geoff} alt="Geoff Watts" />
          <div>
            <p className="semibold medium">FROM THE AUTHOR</p>
            <p className="medium">
              <strong>My job is to help people get from good to great</strong> in their role either
              through my training courses or my coaching practice. I help leaders of all types build
              resilient, reflective organisations and teams whilst also expanding their
              self-awareness and self-management.
              <br />
              <br />
              My coaching cards have been a portable, real-time piece of coaching that thousands of
              people have benefitted from. Turning them into an app makes that even more accessible
              and powerful.
            </p>
            <p className="semibold medium">Geoff Watts, Agile Leadership Coach</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <img src={iaLogo} alt="Inspect and Adapt" />
        <span>© {new Date().getFullYear()} Inspect and Adapt | All Rights Reserved</span>
        <span>
          <a href="https://loopspeed.co.uk" target="_blank" rel="noreferrer">
            Produced by Loopspeed
          </a>{" "}
          and{" "}
          <a href="https://growfox.co.uk" target="_blank" rel="noreferrer">
            Growfox
          </a>
        </span>
      </footer>
    </main>
  )
}

export default IndexPage

const TEAM_ROLES: { icon: string; alt: string }[] = [
  { icon: teamMemberIcon, alt: "Team Member" },
  { icon: agileCoachIcon, alt: "Agile Coach" },
  { icon: professionalCoachIcon, alt: "Pro Coach" },
  { icon: scrumMasterIcon, alt: "Scrum Master" },
  { icon: leaderIcon, alt: "Leader" },
  { icon: productOwnerIcon, alt: "Product Owner" },
]

const BENEFITS_CARDS: { heading: string; text: string; icon: string }[] = [
  {
    heading: "Portable",
    text: "More practical than physical cards. Carry dozens of decks on the go with you.",
    icon: "",
  },
  {
    heading: "Evolving",
    text:
      "Receive free upgrades as more cards get added! The decks continue to evolve based on feedback.",
    icon: "",
  },
  {
    heading: "Inclusive",
    text: "Be part of Geoff's community. Create your own cards and submit them to be added.",
    icon: "",
  },
]
